import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Wallet, Search, Filter, ArrowRight, Calendar, User, MoreVertical, CreditCard } from 'lucide-react';
import { useAppContext } from '../../hooks/useAppContext';
import { API_BASE_URL } from '../../config';

interface CustodySheet {
    id: string;
    custodyNumber: string;
    custodyAmount: number;
    status: 'OPEN' | 'CLOSED';
    notes: string;
    createdAt: string;
    lastModified: string;
    totalSpent: number;
    lineCount: number;
}

const ModernCustodyManagementScreen: React.FC = () => {
    const { user } = useAppContext();
    const navigate = useNavigate();
    const [custodies, setCustodies] = useState<CustodySheet[]>([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);

    // New Custody Form State
    const [newCustodyNumber, setNewCustodyNumber] = useState('');
    const [newCustodyAmount, setNewCustodyAmount] = useState('');
    const [newCustodyNotes, setNewCustodyNotes] = useState('');

    const fetchCustodies = async () => {
        try {
            setLoading(true);
            const headers: any = {};
            if (user) {
                headers['X-User-Id'] = user.id;
                headers['X-User-Role'] = user.role;
            }
            const res = await fetch(`${API_BASE_URL}/instant-expenses/sheets`, { headers });
            if (res.ok) {
                const data = await res.json();
                setCustodies(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            console.error('Error fetching custodies:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCustodies();
    }, [user]);

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const headers: any = { 'Content-Type': 'application/json' };
            if (user) {
                headers['X-User-Id'] = user.id;
                headers['X-User-Role'] = user.role;
            }

            const payload = {
                employeeId: user?.username || user?.id, // Should match backend expectation
                custodyNumber: newCustodyNumber,
                custodyAmount: newCustodyAmount,
                notes: newCustodyNotes
            };

            const res = await fetch(`${API_BASE_URL}/instant-expenses/sheets`, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                setShowCreateModal(false);
                setNewCustodyNumber('');
                setNewCustodyAmount('');
                setNewCustodyNotes('');
                fetchCustodies();
            } else {
                alert('فشل إنشاء العهدة');
            }
        } catch (error) {
            console.error('Error creating custody:', error);
            alert('حدث خطأ أثناء الإنشاء');
        }
    };

    // Derived stats
    const totalActiveAmount = custodies.filter(c => c.status === 'OPEN').reduce((acc, c) => acc + (c.custodyAmount || 0), 0);
    const totalSpent = custodies.filter(c => c.status === 'OPEN').reduce((acc, c) => acc + (c.totalSpent || 0), 0);

    return (
        <div className="p-6 space-y-6 animate-fade-in-up pb-20">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
                        <Wallet className="text-emerald-400" />
                        إدارة العهد والمصروفات
                    </h1>
                    <p className="text-slate-400">متابعة العهد المالية والمصروفات اللحظية</p>
                </div>
                <button
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-600 text-white hover:bg-emerald-500 transition-all hover:scale-105 shadow-lg shadow-emerald-500/20"
                >
                    <Plus size={20} />
                    <span>تسجيل عهدة جديدة</span>
                </button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="glass-card p-6 rounded-2xl relative overflow-hidden bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700/50">
                    <div className="relative z-10">
                        <p className="text-slate-400 text-sm font-medium mb-1">إجمالي العهد المفتوحة</p>
                        <h3 className="text-3xl font-bold text-white">{totalActiveAmount.toLocaleString()} ر.س</h3>
                        <div className="mt-2 text-xs text-emerald-400 flex items-center gap-1">
                            <Wallet size={12} />
                            <span>رصيد متاح للصرف</span>
                        </div>
                    </div>
                    <div className="absolute right-0 top-0 w-32 h-32 bg-emerald-500/10 rounded-full blur-3xl -mr-16 -mt-16"></div>
                </div>

                <div className="glass-card p-6 rounded-2xl relative overflow-hidden bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700/50">
                    <div className="relative z-10">
                        <p className="text-slate-400 text-sm font-medium mb-1">إجمالي المنصرف</p>
                        <h3 className="text-3xl font-bold text-white">{totalSpent.toLocaleString()} ر.س</h3>
                        <div className="mt-2 text-xs text-rose-400 flex items-center gap-1">
                            <CreditCard size={12} />
                            <span>تم صرفه من العهد الحالية</span>
                        </div>
                    </div>
                    <div className="absolute right-0 top-0 w-32 h-32 bg-rose-500/10 rounded-full blur-3xl -mr-16 -mt-16"></div>
                </div>

                <div className="glass-card p-6 rounded-2xl relative overflow-hidden bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700/50">
                    <div className="relative z-10">
                        <p className="text-slate-400 text-sm font-medium mb-1">عدد العهد النشطة</p>
                        <h3 className="text-3xl font-bold text-white">{custodies.filter(c => c.status === 'OPEN').length}</h3>
                        <p className="text-xs text-slate-500 mt-2">عهدة قيد العمل</p>
                    </div>
                </div>
            </div>

            {/* Custodies List */}
            <div className="space-y-4">
                {loading ? (
                    <div className="text-center py-10 text-slate-400">جاري التحميل...</div>
                ) : custodies.length === 0 ? (
                    <div className="text-center py-10 text-slate-400 glass-panel rounded-2xl">
                        <Wallet size={48} className="mx-auto mb-4 opacity-20" />
                        <p>لا توجد عهد مسجلة حالياً</p>
                    </div>
                ) : (
                    custodies.map((custody) => {
                        const progress = custody.custodyAmount > 0
                            ? Math.min(((custody.totalSpent || 0) / custody.custodyAmount) * 100, 100)
                            : 0;
                        const remaining = custody.custodyAmount - (custody.totalSpent || 0);

                        return (
                            <div
                                key={custody.id}
                                onClick={() => navigate(`${custody.id}`)}
                                className="glass-panel p-4 rounded-xl hover:bg-slate-800/80 transition-all cursor-pointer group border border-slate-700/50 hover:border-emerald-500/30"
                            >
                                <div className="flex flex-col md:flex-row justify-between gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className={`p-2 rounded-lg ${custody.status?.toUpperCase() === 'OPEN' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 text-slate-400'}`}>
                                                <Wallet size={20} />
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-white flex items-center gap-2">
                                                    عهدة رقم: {custody.custodyNumber || 'بدون رقم'}
                                                    <span className={`text-xs px-2 py-0.5 rounded-full ${custody.status?.toUpperCase() === 'OPEN' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-slate-700 text-slate-400'}`}>
                                                        {custody.status?.toUpperCase() === 'OPEN' ? 'نشطة' : 'مغلقة'}
                                                    </span>
                                                </h3>
                                                <p className="text-sm text-slate-400 mt-0.5">{custody.notes || 'لا توجد ملاحظات'}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex flex-col md:flex-row items-end md:items-center gap-6">
                                        <div className="text-left min-w-[150px]">
                                            <div className="flex justify-between text-xs mb-1">
                                                <span className="text-slate-400">المنصرف: {custody.totalSpent?.toLocaleString()}</span>
                                                <span className="text-slate-300 font-medium">{Math.round(progress)}%</span>
                                            </div>
                                            <div className="h-2 w-full bg-slate-700 rounded-full overflow-hidden">
                                                <div
                                                    className={`h-full rounded-full transition-all duration-500 ${progress > 90 ? 'bg-rose-500' : 'bg-emerald-500'}`}
                                                    style={{ width: `${progress}%` }}
                                                ></div>
                                            </div>
                                            <div className="mt-1 text-xs text-right text-emerald-400">
                                                المتبقي: {remaining.toLocaleString()} ر.س
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-xs text-slate-500 mb-1">مبلغ العهدة</p>
                                            <p className="text-xl font-bold text-white">{custody.custodyAmount?.toLocaleString()} <span className="text-xs text-slate-500">ر.س</span></p>
                                        </div>
                                        <ArrowRight className="text-slate-600 group-hover:text-emerald-400 transition-colors hidden md:block" />
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Create Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md p-6 shadow-2xl animate-fade-in-up">
                        <h2 className="text-xl font-bold text-white mb-4">إنشاء عهدة جديدة</h2>
                        <form onSubmit={handleCreate} className="space-y-4">
                            <div>
                                <label className="block text-sm text-slate-400 mb-1">رقم/معرف العهدة</label>
                                <input
                                    type="text"
                                    required
                                    value={newCustodyNumber}
                                    onChange={e => setNewCustodyNumber(e.target.value)}
                                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2 text-white focus:outline-none focus:border-emerald-500"
                                    placeholder="مثال: A-101"
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-slate-400 mb-1">المبلغ الإجمالي</label>
                                <input
                                    type="number"
                                    required
                                    value={newCustodyAmount}
                                    onChange={e => setNewCustodyAmount(e.target.value)}
                                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2 text-white focus:outline-none focus:border-emerald-500"
                                    placeholder="0"
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-slate-400 mb-1">ملاحظات</label>
                                <textarea
                                    value={newCustodyNotes}
                                    onChange={e => setNewCustodyNotes(e.target.value)}
                                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2 text-white focus:outline-none focus:border-emerald-500 h-24 resize-none"
                                    placeholder="وصف مختصر للعهدة..."
                                />
                            </div>
                            <div className="flex gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={() => setShowCreateModal(false)}
                                    className="flex-1 px-4 py-2 rounded-xl bg-slate-800 text-slate-300 hover:bg-slate-700 transition-colors"
                                >
                                    إلغاء
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-2 rounded-xl bg-emerald-600 text-white hover:bg-emerald-500 transition-colors shadow-lg shadow-emerald-500/20"
                                >
                                    إنشاء العهدة
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ModernCustodyManagementScreen;
